import React from 'react';
import type { BroadCompetitorData } from './types';

interface BroadCompetitorViewProps {
  data: BroadCompetitorData | null;
}

const BroadCompetitorView: React.FC<BroadCompetitorViewProps> = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-12 text-gray-400">
        <p>No broad competitor data available</p>
      </div>
    );
  }

  const categories = JSON.parse(data.dominant_categories);
  const insights = data.actionable_insights.split('\n').filter((line) => line.trim());

  return (
    <div className="space-y-6">
      {/* Market Overview Section */}
      <div className="bg-[#2a2a2a] rounded-lg p-6 border border-[#E0E0E021]">
        <h2 className="text-xl font-medium text-white mb-4">Market landscape insights</h2>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#4a4a4a]">
            <p className="text-xs text-gray-300 mb-1">Market Price Point</p>
            <p className="text-xl font-medium text-[#EE4423]">{data.market_price_point}</p>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#4a4a4a]">
            <p className="text-xs text-gray-300 mb-1">Competitive Intensity</p>
            <p className="text-xl font-medium text-red-500">{data.competitive_intensity}</p>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#4a4a4a]">
            <p className="text-xs text-gray-300 mb-1">Dominant Categories</p>
            <p className="text-xl font-medium text-white">{categories.length}</p>
          </div>
        </div>

        {/* Categories */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-white mb-3">Dominant Categories</h3>
          <div className="flex flex-wrap gap-2">
            {categories.map((category: string, index: number) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-orange-900/30 text-orange-300 rounded-full text-xs border border-orange-700/50"
              >
                {category}
              </span>
            ))}
          </div>
        </div>

        {/* Landscape Summary */}
        <div>
          <h3 className="text-sm font-medium text-white mb-3">Landscape Summary</h3>
          <p className="text-gray-300 text-sm leading-relaxed">{data.landscape_summary}</p>
        </div>
      </div>

      {/* Actionable Insights */}
      <div className="bg-[#2a2a2a] rounded-lg p-6 border border-[#E0E0E021]">
        <h2 className="text-xl font-medium text-white mb-4">Actionable Insights</h2>
        <div className="space-y-4">
          {insights.map((insight, index) => {
            const cleanedInsight = insight.replace(/^\*\s*/, '').trim();
            if (!cleanedInsight) return null;

            return (
              <div key={index} className="bg-[#1a1a1a] rounded-lg p-4 border border-[#4a4a4a]">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-600 flex items-center justify-center text-white text-xs font-bold">
                    {index + 1}
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed flex-1">{cleanedInsight}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BroadCompetitorView;
